<template>
  <v-container grid-list-lg fluid class="department-edit-page">
    <div class="department-edit-header">
      <div><div class="department-edit-eyebrow">Administration</div><h1>Edit department</h1><p>Organize enrollment, billing, report cards, and parent communication.</p></div>
      <div class="department-edit-actions"><v-btn text @click="close()">Cancel</v-btn><v-btn color="primary" depressed :loading="loading" @click="submit"><v-icon left size="18">mdi-content-save-outline</v-icon>Save changes</v-btn></div>
    </div>

    <v-card-text class="department-edit-content">
      <v-row>
        <v-col cols="12"><v-card elevation="0" class="department-section-card"><v-card-text><div class="department-section-heading"><span><v-icon color="primary" size="20">mdi-office-building-outline</v-icon></span><div><h2>General information</h2><p>The department name and standard monthly fee.</p></div></div><v-row>
        <v-col cols="12" md="8">
          <v-text-field
            outlined
            :hide-details="nameErrors.length === 0"
            dense
            :error-messages="nameErrors"
            v-model="Class.name"
            label="Name"
          ></v-text-field>
        </v-col>

        <v-col cols="12" md="4">
          <v-text-field
            outlined
            dense
            hide-details
            @focus="$event.target.select()"
            class="right-input"
            v-model="Class.defaultFee"
            prefix="$"
            label="Default monthly fee"
          ></v-text-field>
        </v-col>
        </v-row>
        <v-divider class="my-5" />
        <div class="sibling-tier-heading"><div><h3>Sibling fee tiers</h3><p>Apply lower monthly fees when the same parent enrolls multiple active students in this department.</p></div><v-switch v-model="Class.metadata.siblingFeeDiscountEnabled" inset hide-details label="Enabled" class="mt-0" /></div>
        <v-expand-transition><div v-if="Class.metadata.siblingFeeDiscountEnabled" class="sibling-tier-fields">
          <div class="sibling-tier-base"><span>First student</span><strong>{{ Class.defaultFee | currency }}</strong></div>
          <v-row v-for="(amount, index) in Class.metadata.siblingFeeTiers" :key="`sibling-tier-${index}`" align="center">
            <v-col cols="10" sm="6"><v-text-field v-model.number="Class.metadata.siblingFeeTiers[index]" type="number" min="0" step="0.01" prefix="$" outlined dense hide-details :label="`${ordinal(index + 2)} student fee`" /></v-col>
            <v-col cols="2"><v-btn icon color="error" :aria-label="`Remove ${ordinal(index + 2)} student tier`" @click="removeSiblingFeeTier(index)"><v-icon>mdi-delete-outline</v-icon></v-btn></v-col>
          </v-row>
          <v-btn color="primary" outlined small @click="addSiblingFeeTier"><v-icon left>mdi-plus</v-icon>Add next student tier</v-btn>
          <div class="sibling-tier-note">The final amount continues for every additional sibling.</div>
        </div></v-expand-transition>
        </v-card-text></v-card></v-col>

        <v-col cols="12">
          <v-card elevation="0" class="department-section-card">
            <v-card-text>
              <div class="department-section-heading"><span><v-icon color="primary" size="20">mdi-calendar-week-outline</v-icon></span><div><h2>Department schedule</h2><p>Calendar dates on closed days appear bold and red. Include weekend days when needed.</p></div></div>
              <v-row dense>
                <v-col v-for="day in dayOptions" :key="day.value" cols="6" sm="3" md="auto">
                  <v-checkbox v-model="Class.metadata.openWeekdays" :value="day.value" :label="day.text" dense hide-details class="mt-0" />
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12">
          <v-card elevation="0" class="department-section-card annual-charge-card">
            <v-card-text>
              <div class="annual-charge-heading">
                <div>
                  <div class="text-subtitle-1 font-weight-bold">Annual student charge</div>
                  <div class="caption grey--text">Create one open parent invoice each year, with one line for every active student in this department.</div>
                </div>
                <v-switch v-if="Class.metadata.annualCharge" v-model="Class.metadata.annualCharge.enabled" inset hide-details label="Enabled" class="mt-0" />
              </div>
              <v-expand-transition>
                <div v-if="Class.metadata.annualCharge && Class.metadata.annualCharge.enabled" class="annual-charge-fields">
                  <v-row>
                    <v-col cols="12" md="6"><v-text-field v-model="Class.metadata.annualCharge.name" outlined dense hide-details label="Invoice line name" placeholder="Yearly book fee" /></v-col>
                    <v-col cols="12" md="3"><v-text-field v-model.number="Class.metadata.annualCharge.amount" type="number" min="0.01" step="0.01" prefix="$" outlined dense hide-details label="Amount per student" /></v-col>
                    <v-col cols="12" md="3"><v-text-field :value="annualInvoiceDatePreview" outlined dense hide-details readonly label="Expected invoice date" /></v-col>
                    <v-col cols="12" sm="6" md="3"><v-select v-model.number="Class.metadata.annualCharge.startMonth" :items="monthOptions" item-text="text" item-value="value" outlined dense hide-details label="Annual cycle starts — month" /></v-col>
                    <v-col cols="12" sm="6" md="3"><v-select v-model.number="Class.metadata.annualCharge.startDay" :items="dayOfMonthOptions" outlined dense hide-details label="Day" /></v-col>
                    <v-col cols="12" sm="6" md="3"><v-text-field v-model.number="Class.metadata.annualCharge.invoiceDelayDays" type="number" min="0" max="365" outlined dense hide-details label="Create invoice after (days)" /></v-col>
                    <v-col cols="12" sm="6" md="3"><v-text-field v-model.number="Class.metadata.annualCharge.renewalProtectionDays" type="number" min="0" max="180" outlined dense hide-details label="Skip late joins within (days)" /></v-col>
                  </v-row>
                  <v-alert text color="primary" icon="mdi-information-outline" class="mb-0">Late joiners receive the full amount until the renewal-protection window. Students joining inside that window wait for the next annual cycle.</v-alert>
                </div>
              </v-expand-transition>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12"><v-card elevation="0" class="department-section-card"><v-card-text><div class="department-section-heading"><span><v-icon color="primary" size="20">mdi-tune-variant</v-icon></span><div><h2>Program and billing options</h2><p>Control how this department participates in tuition and student workflows.</p></div></div><v-row><v-col cols="12" sm="6" md="4">
          <v-checkbox
            label="Collect Fees in Advance"
            hide-details
            outlined
            dense
            v-model="Class.metadata.preCollectFee"
          ></v-checkbox>
        </v-col>

        <v-col cols="12" sm="6" md="4">
          <v-checkbox
            label="Count In Subscription"
            hide-details
            outlined
            dense
            v-model="Class.countInSubscription"
          ></v-checkbox>
        </v-col>

        <v-col cols="12" sm="6" md="4">
          <v-checkbox
            label="Kids Class Program"
            hide-details
            outlined
            dense
            v-model="Class.isChild"
          ></v-checkbox>
        </v-col>
        </v-row></v-card-text></v-card></v-col>

        <v-col cols="12"><v-card elevation="0" class="department-section-card"><v-card-text><div class="department-section-heading"><span><v-icon color="primary" size="20">mdi-file-chart-outline</v-icon></span><div><h2>Report card display</h2><p>Choose the calculations, feedback, attendance, and signatures shown on report cards.</p></div></div><v-row><v-col cols="12" sm="6" md="6">
          <v-checkbox
            label="Present Final Number for Combined All Terms in Report Card"
            hide-details
            outlined
            dense
            v-model="
              Class.metadata.reportCard.calculateFinalAverageFromAllTerms
            "
          ></v-checkbox>
        </v-col>

        

        <v-col cols="12" sm="6" md="6">
          <v-checkbox
            label="Show Taleem Automated Remarks Based on Progress"
            hide-details
            outlined
            dense
            v-model="Class.metadata.reportCard.showAutomatedRemarks"
          ></v-checkbox>
        </v-col>

        <v-col cols="12" sm="6" md="6">
          <v-checkbox
            label="Show System Attendance on Report Card"
            hide-details
            outlined
            dense
            v-model="Class.metadata.reportCard.showAttendance"
          ></v-checkbox>
        </v-col>

        <v-col cols="12" sm="6" md="6">
          <v-checkbox
            label="Show Percentage on Report Card"
            hide-details
            outlined
            dense
            v-model="Class.metadata.reportCard.showPercentage"
          ></v-checkbox>
        </v-col>

        <v-col cols="12" sm="6" md="6">
          <v-checkbox
            label="Show Grade Guide on Report Card"
            hide-details
            outlined
            dense
            v-model="Class.metadata.reportCard.showGradeGuide"
          ></v-checkbox>
        </v-col>

        <v-col cols="12" sm="6" md="6">
          <v-select
            v-model="Class.metadata.reportCard.feedbackMode"
            :items="feedbackModeItems"
            item-text="text"
            item-value="value"
            outlined
            dense
            hide-details
            label="Feedback Display Mode"
          ></v-select>
        </v-col>

        <v-col cols="12" sm="6" md="6">
          <v-checkbox
            label="Show Teacher Signature"
            hide-details
            outlined
            dense
            v-model="Class.metadata.reportCard.showTeacherSignature"
          ></v-checkbox>
        </v-col>

        <v-col cols="12" sm="6" md="6">
          <v-text-field
            v-model="Class.metadata.reportCard.teacherSignatureLabel"
            outlined
            dense
            hide-details
            label="Teacher Signature Label"
            placeholder="Teacher Signature"
            :disabled="!Class.metadata.reportCard.showTeacherSignature"
          ></v-text-field>
        </v-col>

        <v-col cols="12" sm="6" md="6">
          <v-checkbox
            label="Show Supervisor Signature"
            hide-details
            outlined
            dense
            v-model="Class.metadata.reportCard.showSupervisorSignature"
          ></v-checkbox>
        </v-col>

        <v-col cols="12" sm="6" md="6">
          <v-text-field
            v-model="Class.metadata.reportCard.supervisorSignatureLabel"
            outlined
            dense
            hide-details
            label="Supervisor Signature Label"
            placeholder="Supervisor Signature"
            :disabled="!Class.metadata.reportCard.showSupervisorSignature"
          ></v-text-field>
        </v-col>
        </v-row></v-card-text></v-card></v-col>

        <v-col cols="12">
          <v-card elevation="0" class="department-section-card">
            <v-card-text>
              <div class="department-section-heading"><span><v-icon color="primary" size="20">mdi-draw-pen</v-icon></span><div><h2>Principal signature</h2><p>Upload the signature used on this department’s report cards.</p></div></div>

              <v-file-input
                accept="image/png,image/jpeg,image/jpg,image/webp"
                outlined
                dense
                hide-details
                prepend-icon=""
                prepend-inner-icon="mdi-upload"
                label="Upload Signature"
                @change="onSignatureSelected"
              ></v-file-input>

              <div
                v-if="
                  Class.metadata.reportCard.principalSignature &&
                  Class.metadata.reportCard.principalSignature.base64
                "
                class="mt-4"
              >
                <div class="mb-2 text-caption grey--text">
                  {{
                    Class.metadata.reportCard.principalSignature.fileName ||
                    "Signature uploaded"
                  }}
                </div>

                <v-img
                  :src="Class.metadata.reportCard.principalSignature.base64"
                  contain
                  max-height="120"
                  max-width="320"
                  class="grey lighten-4 pa-2 rounded"
                ></v-img>

                <div class="mt-3">
                  <v-btn small color="error" outlined @click="removeSignature">
                    Remove Signature
                  </v-btn>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12">
          <v-card elevation="0" class="department-section-card">
            <v-card-text>
              <v-layout row wrap align-center class="mb-3">
                <v-flex>
                  <div class="department-section-heading mb-0"><span><v-icon color="primary" size="20">mdi-chart-bar</v-icon></span><div><h2>Report card grade ranges</h2><p>
                    These ranges will be used for this department only. If
                    empty, system default ranges will be used.
                  </p></div></div>
                </v-flex>
                <v-flex shrink>
                  <v-btn color="success" small @click="addGradeBand"
                    >Add Range</v-btn
                  >
                </v-flex>
              </v-layout>

              <v-row
                v-for="(band, index) in Class.metadata.reportCard.gradeBands"
                :key="index"
                class="align-center"
              >
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="band.label"
                    outlined
                    dense
                    hide-details
                    label="Label"
                    placeholder="Excellent"
                  ></v-text-field>
                </v-col>

                <v-col cols="6" md="3">
                  <v-text-field
                    v-model.number="band.min"
                    type="number"
                    outlined
                    dense
                    hide-details
                    label="Min %"
                  ></v-text-field>
                </v-col>

                <v-col cols="6" md="3">
                  <v-text-field
                    v-model.number="band.max"
                    type="number"
                    outlined
                    dense
                    hide-details
                    label="Max %"
                  ></v-text-field>
                </v-col>

                <v-col cols="12" md="2" class="text-right">
                  <v-btn
                    icon
                    color="error"
                    @click="removeGradeBand(index)"
                    :disabled="
                      Class.metadata.reportCard.gradeBands.length === 1
                    "
                  >
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12">
          <v-card elevation="0" class="department-section-card"><v-card-text><v-layout row wrap align-center>
            <v-flex xs9> Custom Questions </v-flex>
            <v-spacer></v-spacer>
            <v-flex xs3 shrink class="text-right">
              <v-btn color="primary" outlined @click="addRow"><v-icon left>mdi-plus</v-icon>Add question</v-btn>
            </v-flex>
          </v-layout><div class="caption grey--text mt-1">Collect department-specific information during the application process.</div></v-card-text></v-card>
        </v-col>

        <v-col cols="12">
          <draggable
            v-if="Class && Class.metadata && Class.metadata.customValue"
            tag="ul"
            :list="Class.metadata.customValue"
            class="list-group"
          >
            <v-card
              class="mb-4"
              outlined
              v-for="(element, idx) in Class.metadata.customValue"
              :key="idx"
            >
              <v-card-text>
                <v-layout row wrap align-center>
                  <v-flex xs9>
                    <v-text-field
                      v-model="element.question"
                      autofocus
                      hide-details
                      outlined
                      dense
                      placeholder="Untitled Question"
                    ></v-text-field>
                  </v-flex>

                  <v-flex xs3>
                    <v-select
                      @change="dropboxCharge(element, idx)"
                      hide-details
                      :items="element.selectAnswerType"
                      v-model="element.selectedAnswerType"
                      dense
                      outlined
                    ></v-select>
                  </v-flex>

                  <v-flex xs9>
                    <v-text-field
                      v-model="element.answer"
                      disabled
                      hide-details
                      outlined
                      v-if="element.selectedAnswerType === 'Short answer'"
                      dense
                      placeholder="Short answer text"
                    ></v-text-field>

                    <div
                      v-if="
                        element.selectedAnswerType == 'Checkboxes' ||
                        element.selectedAnswerType == 'Multiple choice' ||
                        element.selectedAnswerType == 'Dropdown'
                      "
                    >
                      <v-layout
                        align-center
                        row
                        wrap
                        v-for="(list, ind) in element.ChoiceList"
                        :key="ind"
                      >
                        <v-flex
                          shrink
                          v-if="element.selectedAnswerType === 'Checkboxes'"
                        >
                          <v-checkbox
                            :key="ind"
                            readonly
                            hide-details
                            class="shrink mx-0"
                          ></v-checkbox>
                        </v-flex>

                        <v-flex
                          shrink
                          v-if="
                            element.selectedAnswerType === 'Multiple choice'
                          "
                        >
                          <v-radio
                            :key="ind"
                            readonly
                            hide-details
                            class="shrink mt-5"
                          ></v-radio>
                        </v-flex>

                        <v-flex>
                          <v-text-field
                            v-model="list.label"
                            hide-details
                            v-if="element.ChoiceList.length > 1"
                            @click:append="removeOption(ind, element)"
                            @focus="$event.target.select()"
                            append-icon="mdi-delete"
                            :placeholder="list.label"
                          >
                          </v-text-field>

                          <v-text-field
                            @focus="$event.target.select()"
                            v-model="list.label"
                            hide-details
                            v-else
                            :placeholder="list.label"
                          >
                          </v-text-field>
                        </v-flex>
                      </v-layout>

                      <v-layout row wrap>
                        <v-flex>
                          <v-btn text @click="addInList(element)"
                            >Add option</v-btn
                          >
                        </v-flex>
                      </v-layout>
                    </div>
                  </v-flex>
                </v-layout>
              </v-card-text>

              <v-divider></v-divider>

              <v-card-actions>
                <v-layout row wrap align-center>
                  <v-spacer></v-spacer>
                  <v-flex shrink>
                    <v-btn fab text small @click="removeQuestion(idx)">
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                  </v-flex>
                  <v-divider vertical></v-divider>
                  <v-flex shrink>
                    <v-switch
                      hide-details
                      class="px-3 mt-0"
                      v-model="element.isRequired"
                      inset
                      label="Required"
                    ></v-switch>
                  </v-flex>
                </v-layout>
              </v-card-actions>
            </v-card>
          </draggable>
        </v-col>

        <v-col cols="12">
          <v-card elevation="0" class="department-section-card">
            <v-card-text>
              <div class="department-section-heading"><span><v-icon color="primary" size="20">mdi-link-variant</v-icon></span><div><h2>Public application link</h2><p>Share this link to send applicants directly to this department.</p></div></div>
              <v-layout row wrap align-center>
                <v-flex>
                  <a :href="href" target="_blank">
                    https://portal.taleem.app/dep/{{ currentBusiness.code }}/{{
                      Class.id
                    }}
                  </a>
                </v-flex>
                <v-flex shrink>
                  <v-btn outlined small @click="copyLink" fab>
                    <v-icon small>mdi-content-copy</v-icon>
                  </v-btn>
                </v-flex>
              </v-layout>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="12" class="department-editor-section">
          <div class="department-editor-heading">
            <h2>Application contact information</h2>
            <p>
              This information will be sent to anyone who fills out the
              application via email.
            </p>
          </div>
          <quill-editor
            :content="Class.contactInformation"
            :options="editorOption"
            @change="onContactInformationChange($event)"
          />
        </v-col>

        <v-col cols="12" sm="12" class="department-editor-section">
          <div class="department-editor-heading">
            <h2>Pending-balance letter</h2>
            <p>
              This information will be sent to anyone whose balance is pending
              for this department.
            </p>
          </div>
          <quill-editor
            :content="Class.letterContactInformation"
            :options="editorOption"
            @change="onLetterContactInformationChange($event)"
          />
        </v-col>

        <v-col cols="12" sm="12" class="department-editor-section">
          <div class="department-editor-heading"><h2>Department description</h2><p>Public-facing details about this department.</p></div>
          <quill-editor
            :content="Class.description"
            :options="editorOption"
            @change="onEditorChange($event)"
          />
        </v-col>
      </v-row>
    </v-card-text>

  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import classService from "../service.js";
import copy from "copy-to-clipboard";
import draggable from "vuedraggable";
import { validationMixin } from "vuelidate";
import { required } from "vuelidate/lib/validators";
import "quill/dist/quill.core.css";
import "quill/dist/quill.snow.css";
import "quill/dist/quill.bubble.css";
import { quillEditor } from "vue-quill-editor";

export default {
  props: {
    editDialog: {
      default: false,
    },
    editClass: {
      type: Object,
    },
  },
  name: "update-Class",
  data() {
    return {
      feedbackModeItems: [
        { text: "Show All Term Feedback", value: "all" },
        { text: "Show Only Last Term Feedback", value: "last-term-only" },
      ],
      dayOptions: [
        { text: "Sunday", value: 0 },
        { text: "Monday", value: 1 },
        { text: "Tuesday", value: 2 },
        { text: "Wednesday", value: 3 },
        { text: "Thursday", value: 4 },
        { text: "Friday", value: 5 },
        { text: "Saturday", value: 6 },
      ],
      monthOptions: [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December",
      ].map((text, index) => ({ text, value: index + 1 })),
      dayOfMonthOptions: Array.from({ length: 31 }, (value, index) => index + 1),
      showAttendance: true,
      showPercentage: true,
      href: null,
      editorOption: {
        placeholder: "Description",
      },
      answerType: [
        "Short answer",
        "Paragraph",
        "Checkboxes",
        "Multiple choice",
        "Dropdown",
      ],
      loading: false,
      dialog: false,
      Class: {
        name: null,
        defaultFee: null,
        note: null,
        contactInformation: null,
        letterContactInformation: null,
        description: null,
        metadata: {
          customValue: [],
          preCollectFee: false,
          openWeekdays: [1, 2, 3, 4, 5],
          annualCharge: {
            enabled: false,
            name: "",
            amount: 0,
            startMonth: 9,
            startDay: 1,
            invoiceDelayDays: 15,
            renewalProtectionDays: 45,
          },
          siblingFeeDiscountEnabled: false,
          siblingFeeTiers: [],
          reportCard: {
            calculateFinalAverageFromAllTerms: false,
            showAutomatedRemarks: true,
            showAttendance: true,
            showPercentage: true,
            showGradeGuide: true,
            feedbackMode: "all",
            showTeacherSignature: true,
            teacherSignatureLabel: "Teacher Signature",
            showSupervisorSignature: true,
            supervisorSignatureLabel: "Supervisor Signature",
            principalSignature: {
              fileName: null,
              mimeType: null,
              base64: null,
            },
            gradeBands: [
              { label: "Excellent", min: 90, max: 100 },
              { label: "Very Good", min: 80, max: 89.99 },
              { label: "Good", min: 75, max: 79.99 },
              { label: "Satisfactory", min: 65, max: 74.99 },
              { label: "Fail", min: 0, max: 64.99 },
            ],
          },
        },
      },
    };
  },
  mixins: [validationMixin],
  validations: {
    Class: {
      name: {
        required,
      },
    },
  },
  created() {
    this.loadData();
  },
  components: { quillEditor, draggable },
  computed: {
    ...mapGetters("global", ["currentUser", "currentBusiness"]),
    nameErrors() {
      const errors = [];
      if (!this.$v.Class.name.$dirty) return errors;
      if (!this.$v.Class.name.required) {
        errors.push(this.$t("validations.fieldIsRequired"));
      }
      return errors;
    },
    annualInvoiceDatePreview() {
      const rule = this.Class.metadata.annualCharge;
      if (!rule || !rule.startMonth || !rule.startDay) return "—";
      const start = this.$moment.utc([2024, Number(rule.startMonth) - 1, 1]);
      start.date(Math.min(Number(rule.startDay), start.daysInMonth()));
      return start.add(Number(rule.invoiceDelayDays || 0), "days").format("MMMM D");
    },
  },
  watch: {
    editDialog(val) {
      this.Class = this.normalizeClass(this.editClass);
      this.dialog = val;
    },
  },
  methods: {
    getDefaultGradeBands() {
      return [
        { label: "Excellent", min: 90, max: 100 },
        { label: "Very Good", min: 80, max: 89.99 },
        { label: "Good", min: 75, max: 79.99 },
        { label: "Satisfactory", min: 65, max: 74.99 },
        { label: "Fail", min: 0, max: 64.99 },
      ];
    },

    normalizeClass(classData = {}) {
      const base = JSON.parse(
        JSON.stringify(
          classData && Object.keys(classData).length
            ? classData
            : {
                name: null,
                defaultFee: null,
                note: null,
                contactInformation: null,
                letterContactInformation: null,
                description: null,
                metadata: {},
              }
        )
      );

      if (!base.metadata) {
        base.metadata = {};
      }

      if (!Object.prototype.hasOwnProperty.call(base.metadata, "customValue")) {
        base.metadata.customValue = [];
      }

      if (
        !Object.prototype.hasOwnProperty.call(base.metadata, "preCollectFee")
      ) {
        base.metadata.preCollectFee = false;
      }

      if (!Array.isArray(base.metadata.openWeekdays)) {
        base.metadata.openWeekdays = [1, 2, 3, 4, 5];
      }

      if (!base.metadata.reportCard) {
        base.metadata.reportCard = {};
      }

      base.metadata.annualCharge = Object.assign(
        {
          enabled: false,
          name: "",
          amount: 0,
          startMonth: 9,
          startDay: 1,
          invoiceDelayDays: 15,
          renewalProtectionDays: 45,
        },
        base.metadata.annualCharge || {}
      );
      base.metadata.siblingFeeDiscountEnabled =
        base.metadata.siblingFeeDiscountEnabled === true;
      base.metadata.siblingFeeTiers = Array.isArray(base.metadata.siblingFeeTiers)
        ? base.metadata.siblingFeeTiers.map((amount) => Number(amount))
        : [];

      if (
        !Object.prototype.hasOwnProperty.call(
          base.metadata.reportCard,
          "showGradeGuide"
        )
      ) {
        base.metadata.reportCard.showGradeGuide = true;
      }

      if (
        !Object.prototype.hasOwnProperty.call(
          base.metadata.reportCard,
          "feedbackMode"
        )
      ) {
        base.metadata.reportCard.feedbackMode = "all";
      }

      if (
        !Object.prototype.hasOwnProperty.call(
          base.metadata.reportCard,
          "showTeacherSignature"
        )
      ) {
        base.metadata.reportCard.showTeacherSignature = true;
      }

      if (
        !Object.prototype.hasOwnProperty.call(
          base.metadata.reportCard,
          "teacherSignatureLabel"
        )
      ) {
        base.metadata.reportCard.teacherSignatureLabel = "Teacher Signature";
      }

      if (
        !Object.prototype.hasOwnProperty.call(
          base.metadata.reportCard,
          "showSupervisorSignature"
        )
      ) {
        base.metadata.reportCard.showSupervisorSignature = true;
      }

      if (
        !Object.prototype.hasOwnProperty.call(
          base.metadata.reportCard,
          "supervisorSignatureLabel"
        )
      ) {
        base.metadata.reportCard.supervisorSignatureLabel =
          "Supervisor Signature";
      }

      if (
        !Object.prototype.hasOwnProperty.call(
          base.metadata.reportCard,
          "showAttendance"
        )
      ) {
        base.metadata.reportCard.showAttendance = true;
      }

      if (
        !Object.prototype.hasOwnProperty.call(
          base.metadata.reportCard,
          "showPercentage"
        )
      ) {
        base.metadata.reportCard.showPercentage = true;
      }

      if (
        !Object.prototype.hasOwnProperty.call(
          base.metadata.reportCard,
          "calculateFinalAverageFromAllTerms"
        )
      ) {
        base.metadata.reportCard.calculateFinalAverageFromAllTerms = false;
      }

      if (
        !Object.prototype.hasOwnProperty.call(
          base.metadata.reportCard,
          "showAutomatedRemarks"
        )
      ) {
        base.metadata.reportCard.showAutomatedRemarks = true;
      }

      if (
        !Array.isArray(base.metadata.reportCard.gradeBands) ||
        !base.metadata.reportCard.gradeBands.length
      ) {
        base.metadata.reportCard.gradeBands = this.getDefaultGradeBands();
      } else {
        base.metadata.reportCard.gradeBands =
          base.metadata.reportCard.gradeBands.map((row) => ({
            label: row.label || "",
            min:
              row.min !== undefined && row.min !== null ? Number(row.min) : 0,
            max:
              row.max !== undefined && row.max !== null ? Number(row.max) : 0,
          }));
      }

      if (!base.metadata.reportCard.principalSignature) {
        base.metadata.reportCard.principalSignature = {
          fileName: null,
          mimeType: null,
          base64: null,
        };
      } else {
        base.metadata.reportCard.principalSignature = {
          fileName:
            base.metadata.reportCard.principalSignature.fileName || null,
          mimeType:
            base.metadata.reportCard.principalSignature.mimeType || null,
          base64: base.metadata.reportCard.principalSignature.base64 || null,
        };
      }

      return base;
    },

    addGradeBand() {
      this.Class.metadata.reportCard.gradeBands.push({
        label: "",
        min: 0,
        max: 0,
      });
    },

    ordinal(value) {
      const mod100 = value % 100;
      if (mod100 >= 11 && mod100 <= 13) return `${value}th`;
      return `${value}${{ 1: "st", 2: "nd", 3: "rd" }[value % 10] || "th"}`;
    },

    addSiblingFeeTier() {
      const tiers = this.Class.metadata.siblingFeeTiers;
      tiers.push(tiers.length ? tiers[tiers.length - 1] : Number(this.Class.defaultFee || 0));
    },

    removeSiblingFeeTier(index) {
      this.Class.metadata.siblingFeeTiers.splice(index, 1);
    },

    removeGradeBand(index) {
      this.Class.metadata.reportCard.gradeBands.splice(index, 1);
    },

    async onSignatureSelected(file) {
      if (!file) return;

      const allowedTypes = [
        "image/png",
        "image/jpeg",
        "image/jpg",
        "image/webp",
      ];

      if (!allowedTypes.includes(file.type)) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: "Please upload a PNG, JPG, or WEBP image",
          });
        return;
      }

      try {
        const base64 = await this.fileToBase64(file);

        this.Class.metadata.reportCard.principalSignature = {
          fileName: file.name,
          mimeType: file.type,
          base64,
        };
      } catch (error) {
        console.log("signature upload error", error);
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: "Unable to read signature file",
          });
      }
    },

    fileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    },

    removeSignature() {
      this.Class.metadata.reportCard.principalSignature = {
        fileName: null,
        mimeType: null,
        base64: null,
      };
    },

    removeOption(index, element) {
      element.ChoiceList.splice(index, 1);
    },

    removeQuestion(index) {
      this.Class.metadata.customValue.splice(index, 1);
    },

    dropboxCharge(element) {
      if (!element.ChoiceList) {
        this.addValueToList(element);
      }
    },

    addValueToList(element) {
      if (!element.ChoiceList) {
        this.$set(element, "ChoiceList", []);
      }
      element.ChoiceList.push({
        label: `Option ${element.ChoiceList.length + 1}`,
        answer: null,
      });
    },

    addRow() {
      if (!this.Class.metadata) {
        this.$set(this.Class, "metadata", {});
      }

      if (
        !Object.prototype.hasOwnProperty.call(
          this.Class.metadata,
          "customValue"
        )
      ) {
        this.$set(this.Class.metadata, "customValue", []);
      }

      this.Class.metadata.customValue.push({
        question: null,
        answer: null,
        selectAnswerType: this.answerType,
        selectedAnswerType: "Short answer",
        isRequired: false,
      });
    },

    addInList(element) {
      this.addValueToList(element);
    },

    copyLink() {
      copy(this.href);
      this.$swal
        .mixin({
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
        })
        .fire({
          icon: "success",
          title: "Link is copy!",
        });
    },

    close() {
      this.empty();
      this.$router.go(-1);
    },

    loadData() {
      const { id } = this.$route.params;
      return classService
        .getById(id)
        .then((response) => {
          this.Class = this.normalizeClass(response.data);
          this.href = `https://portal.taleem.app/dep/${this.currentBusiness.code}/${this.Class.id}`;
          return response;
        })
        .catch((error) => {
          console.log("error", error);
        });
    },

    empty() {
      this.Class = this.normalizeClass({
        name: null,
        defaultFee: null,
        note: null,
        contactInformation: null,
        letterContactInformation: null,
        description: null,
        metadata: {},
      });
      this.$v.$reset();
    },

    onEditorChange(value) {
      this.Class.description = value.html;
    },

    onContactInformationChange(value) {
      this.Class.contactInformation = value.html;
    },

    onLetterContactInformationChange(value) {
      this.Class.letterContactInformation = value.html;
    },

    submit() {
      this.$v.$touch();

      const annual = this.Class.metadata.annualCharge;
      const annualInvalid = annual.enabled &&
        (!(annual.name || "").trim() || Number(annual.amount) <= 0 ||
          Number(annual.invoiceDelayDays) < 0 || Number(annual.renewalProtectionDays) < 0);
      const siblingInvalid = this.Class.metadata.siblingFeeDiscountEnabled &&
        (!this.Class.metadata.siblingFeeTiers.length ||
          this.Class.metadata.siblingFeeTiers.some((amount) =>
            amount === "" || amount === null || !Number.isFinite(Number(amount)) || Number(amount) < 0
          ));
      if (this.$v.$invalid || annualInvalid || siblingInvalid) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: annualInvalid ? "Complete the annual charge settings" : siblingInvalid ? "Add a valid sibling fee tier" : this.$t("toastMessage.text.invalidFields"),
          });
        return;
      }

      this.loading = true;

      if (
        this.Class &&
        this.Class.metadata &&
        this.Class.metadata.customValue
      ) {
        this.Class.metadata.customValue =
          this.Class.metadata.customValue.filter((row) => row.question);
      }

      if (this.Class && this.Class.metadata && this.Class.metadata.reportCard) {
        const rc = this.Class.metadata.reportCard;

        rc.feedbackMode =
          rc.feedbackMode === "last-term-only" ? "last-term-only" : "all";

        rc.teacherSignatureLabel =
          (rc.teacherSignatureLabel || "").trim() || "Teacher Signature";

        rc.supervisorSignatureLabel =
          (rc.supervisorSignatureLabel || "").trim() || "Supervisor Signature";
      }

      return classService
        .update(this.Class.id, this.Class)
        .then((result) => {
          if (result.status) {
            this.$swal
              .mixin({
                toast: true,
                position: "top-end",
                showConfirmButton: false,
                timer: 3000,
              })
              .fire({
                icon: "success",
                title: "Department is updated",
              });
            this.loading = false;
            this.$emit("submit");
            this.close();
          }
        })
        .catch((err) => {
          this.loading = false;

          const message =
            err?.response?.data?.message ||
            err?.message ||
            "Unable to update department";

          this.$swal
            .mixin({
              toast: true,
              position: "top-end",
              showConfirmButton: false,
              timer: 3000,
            })
            .fire({
              icon: "error",
              title: message,
            });
        });
    },
  },
};
</script>

<style scoped>
.department-edit-page { min-height: 100%; padding: 30px 36px 48px; background: #f6f8f6; }
.department-edit-header { display: flex; align-items: flex-end; justify-content: space-between; gap: 20px; max-width: 1440px; margin: 0 auto 22px; }
.department-edit-eyebrow { margin-bottom: 5px; color: #43a047; font-size: 11px; font-weight: 700; letter-spacing: .1em; text-transform: uppercase; }
.department-edit-header h1 { margin: 0; color: #172033; font-size: 29px; line-height: 1.2; letter-spacing: -.025em; }
.department-edit-header p { margin: 6px 0 0; color: #728095; font-size: 13px; }
.department-edit-actions { display: flex; align-items: center; gap: 8px; }
.department-edit-actions .v-btn { height: 42px; border-radius: 9px; text-transform: none; letter-spacing: 0; }
.department-edit-content { max-width: 1440px; margin: 0 auto; padding: 0 !important; }
.department-section-card { overflow: hidden; border: 1px solid #e3e9e3; border-radius: 13px !important; background: #fff; }
.department-section-card > .v-card__text { padding: 22px 24px; }
.department-section-heading { display: flex; align-items: flex-start; gap: 12px; margin-bottom: 20px; }
.department-section-heading > span { display: flex; width: 38px; height: 38px; flex: 0 0 38px; align-items: center; justify-content: center; border-radius: 9px; background: #eaf6eb; }
.department-section-heading h2, .department-editor-heading h2 { margin: 0; color: #253047; font-size: 16px; line-height: 1.3; }
.department-section-heading p, .department-editor-heading p { margin: 3px 0 0; color: #7b8799; font-size: 12px; line-height: 1.5; }
.department-section-card >>> .v-input__slot, .department-editor-section >>> .v-input__slot { border-radius: 8px; }
.annual-charge-card { border-color: #cfe3d0; }
.sibling-tier-heading { display: flex; align-items: center; justify-content: space-between; gap: 18px; }
.sibling-tier-heading h3 { margin: 0; color: #253047; font-size: 15px; }
.sibling-tier-heading p { margin: 3px 0 0; color: #7b8799; font-size: 12px; line-height: 1.5; }
.sibling-tier-fields { margin-top: 18px; }
.sibling-tier-base { display: flex; max-width: 360px; align-items: center; justify-content: space-between; margin-bottom: 12px; padding: 12px 14px; border-radius: 8px; background: #f4f8f4; color: #465568; }
.sibling-tier-note { display: inline-block; margin-left: 12px; color: #7b8799; font-size: 12px; }
.annual-charge-heading { display: flex; align-items: center; justify-content: space-between; gap: 18px; }
.annual-charge-fields { margin-top: 20px; padding-top: 20px; border-top: 1px solid #edf1ed; }
.annual-charge-card >>> .v-input__slot { border-radius: 8px; }
.department-editor-section { margin: 12px 12px 0; padding: 22px 24px !important; border: 1px solid #e3e9e3; border-radius: 13px; background: #fff; }
.department-editor-heading { margin-bottom: 14px; }
.department-edit-page >>> .ql-toolbar.ql-snow { border-color: #e3e9e3; border-radius: 8px 8px 0 0; }
.department-edit-page >>> .ql-container.ql-snow { min-height: 130px; border-color: #e3e9e3; border-radius: 0 0 8px 8px; }
.department-edit-page >>> .v-btn { text-transform: none; letter-spacing: 0; }
@media (max-width: 600px) {
  .department-edit-page { padding: 20px 12px 36px; }
  .department-edit-header { align-items: stretch; flex-direction: column; }
  .department-edit-actions { justify-content: flex-end; }
  .department-edit-header h1 { font-size: 25px; }
  .department-section-card > .v-card__text { padding: 18px 16px; }
  .department-editor-section { margin-right: 12px; margin-left: 12px; padding: 18px 16px !important; }
  .annual-charge-heading { align-items: flex-start; flex-direction: column; }
  .sibling-tier-heading { align-items: flex-start; flex-direction: column; }
  .sibling-tier-note { display: block; margin: 10px 0 0; }
}
</style>
